/**
 * Why a thumbnail request could not be honoured, in the user's terms (pure).
 * One map for the run and the refine, because the two share most codes.
 */
import { ApiError } from "../../core/api.js";

export function describeThumbnailError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : "Something went wrong.";
  }
  switch (error.code) {
    case "thumbnails-not-configured":
      return "AI is not configured on this server yet (no OpenRouter key).";
    case "image-generation-unsupported":
      return "This server's AI model can't generate images. Pick an image model below, or ask an admin to set one.";
    case "no-portrait":
      return "Add a portrait first — every thumbnail is built around a face. (An admin can add presenter photos on /admin instead, and every thumbnail picks its own.)";
    case "no-script":
      return "There's no script yet. Import one, or type the title and the thumbnail text yourself.";
    case "invalid-model":
      return "That model id doesn't look right. Pick one from the list.";
    case "already-running":
      return "Thumbnails are already being generated on this board.";
    case "shot-not-found":
      return "That thumbnail is gone — someone may have removed it.";
    case "feedback-empty":
      return "Say what should change first.";
    case "unusable-response":
      return "The model didn't return a usable picture. Try again, or say it differently.";
    case "rate-limited":
    case "too-many-concurrent":
      return "You've generated a lot recently — give it a minute.";
    case "server-draining":
      return "The server is restarting. Try again in a few seconds.";
    case "upstream-timeout":
      return "The image model took too long. Try again, or pick a faster model.";
    case "upstream-unreachable":
      return "Couldn't reach the AI provider. Check the server's network and try again.";
    case "upstream-unauthorized":
      return "The AI provider rejected this server's key. An admin needs to check it.";
    case "upstream-payment-required":
      return "The AI provider account is out of credit.";
    case "upstream-rate-limited":
      return "The AI provider is rate-limiting this server. Give it a minute.";
    case "upstream-model-rejected":
      return "That model won't take this request — it may not do image editing. Pick another image model.";
    case "upstream-error":
      return "The AI provider failed. Try again in a moment.";
    default:
      return error.message;
  }
}
